import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { Papa } from 'ngx-papaparse';
import { MessageService } from 'primeng/api';
import { CommonEntityService } from 'src/app/common/services/common-entity.service';
import {
  CsvValidationResult,
  buildCollectionName,
  chunkIds,
  validateCsvRows,
} from './collections-import.helpers';

interface PlannedCollection {
  name: string;
  ids: number[];
}

interface ImportResult {
  name: string;
  id?: number;
  itemCount: number;
  error?: string;
}

type Step = 'upload' | 'preview' | 'dry-run' | 'importing' | 'results';

@Component({
  selector: 'app-collections-bulk-import',
  templateUrl: './collections-bulk-import.component.html',
  styleUrls: ['./collections-bulk-import.component.sass'],
  providers: [MessageService],
})
export class CollectionsBulkImportComponent {
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  private static readonly ID_COLUMN = 'item_id';
  private static readonly ITEM_LOOKUP_BATCH_SIZE = 300;

  step: Step = 'upload';

  itemsPerCollection: number = 100;
  nameTemplate: string = '';

  parsedRows: any[] = [];
  validation: CsvValidationResult | null = null;

  plannedCollections: PlannedCollection[] = [];
  dryRunErrors: string[] = [];
  isRunningDryRun: boolean = false;
  private itemIdToObjectId: Map<number, string> = new Map();

  isImporting: boolean = false;
  importProgress: number = 0;
  results: ImportResult[] = [];

  constructor(
    private papa: Papa,
    private commonEntityService: CommonEntityService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
  ) {}

  onFileChange(event: any) {
    const file = event.target.files?.[0];
    if (!file) return;
    this.reset();

    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.papa.parse(e.target.result, {
        header: true,
        skipEmptyLines: 'greedy',
        complete: (res: any) => this.handleParsedRows(res.data),
        error: (err: any) => {
          this.messageService.add({ severity: 'error', summary: 'Parse Error', detail: err.message });
        },
      });
    };
    reader.readAsText(file);
  }

  handleParsedRows(rows: any[]) {
    this.parsedRows = rows || [];
  }

  get canValidate(): boolean {
    return this.parsedRows.length > 0 && this.itemsPerCollection > 0 && !!this.nameTemplate;
  }

  validate() {
    this.validation = validateCsvRows(this.parsedRows, CollectionsBulkImportComponent.ID_COLUMN, this.nameTemplate);
    if (this.validation.valid) {
      this.step = 'preview';
    }
  }

  async runDryRun() {
    this.isRunningDryRun = true;
    this.dryRunErrors = [];
    this.plannedCollections = [];

    const orderedIds = this.validation!.orderedIds;
    const idToObjectId = new Map<number, string>();

    for (let i = 0; i < orderedIds.length; i += CollectionsBulkImportComponent.ITEM_LOOKUP_BATCH_SIZE) {
      const batch = orderedIds.slice(i, i + CollectionsBulkImportComponent.ITEM_LOOKUP_BATCH_SIZE);
      const records = await this.commonEntityService.findAllWithQuery('items', {
        query: { id: { $in: batch } },
        select: 'id _id',
        autopopulate: false,
        virtuals: false,
      });
      for (const record of records || []) {
        idToObjectId.set(record.id, record._id);
      }
    }

    const missing = orderedIds.filter((id) => !idToObjectId.has(id));
    if (missing.length) {
      this.dryRunErrors.push(`${missing.length} item id(s) not found in this environment: ${missing.join(', ')}`);
    }

    const chunks = chunkIds(orderedIds, this.itemsPerCollection);
    const planned: PlannedCollection[] = chunks.map((ids, i) => ({
      name: buildCollectionName(this.nameTemplate, i + 1),
      ids,
    }));

    const plannedNames = planned.map((p) => p.name);
    const collisions = await this.commonEntityService.findAllWithQuery('collections', {
      query: { name: { $in: plannedNames } },
      select: 'name',
    });
    if (collisions && collisions.length) {
      this.dryRunErrors.push(
        `${collisions.length} planned collection name(s) already exist: ${collisions.map((c: any) => c.name).join(', ')}`,
      );
    }

    this.itemIdToObjectId = idToObjectId;
    this.plannedCollections = planned;
    this.isRunningDryRun = false;
    this.step = 'dry-run';
  }

  get dryRunPassed(): boolean {
    return this.dryRunErrors.length === 0 && this.plannedCollections.length > 0;
  }

  async runImport() {
    this.step = 'importing';
    this.isImporting = true;
    this.importProgress = 0;
    this.results = [];

    for (let i = 0; i < this.plannedCollections.length; i++) {
      const { name, ids } = this.plannedCollections[i];
      try {
        const created = await this.commonEntityService.create('collections', {
          name,
          items_ref: ids.map((id) => this.itemIdToObjectId.get(id)),
          enabled: true,
        });
        try {
          // The API's own async worker sync (Item.collection_ref) isn't reliable enough to
          // depend on — reconcile it here so the collection's items show up immediately.
          await this.commonEntityService.cleanItemCollectionRelationship('scripts/item-collection/collection', created.id);
        } catch {
          // Non-fatal: items_ref is already correct; this only affects the reverse-lookup
          // "Collection Items" grid, which can still be fixed later via "Force sync".
        }
        this.results.push({ name, id: created?.id, itemCount: ids.length });
      } catch (e: any) {
        this.results.push({ name, itemCount: ids.length, error: e?.error?.message || 'Failed to create' });
      }
      this.importProgress = Math.floor(((i + 1) / this.plannedCollections.length) * 100);
      this.cdr.detectChanges();
    }

    this.isImporting = false;
    this.step = 'results';
  }

  get successCount(): number {
    return this.results.filter((r) => !r.error).length;
  }

  get failureCount(): number {
    return this.results.filter((r) => r.error).length;
  }

  downloadTemplate() {
    const content = 'item_id\n3610\n5167\n10894';
    const blob = new Blob([content], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'collections_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  back() {
    if (this.step === 'preview') this.step = 'upload';
    else if (this.step === 'dry-run') this.step = 'preview';
  }

  close() {
    this.visibleChange.emit(false);
    this.reset();
  }

  private reset() {
    this.step = 'upload';
    this.parsedRows = [];
    this.validation = null;
    this.plannedCollections = [];
    this.dryRunErrors = [];
    this.itemIdToObjectId = new Map();
    this.results = [];
    this.importProgress = 0;
  }
}
